'use client';
import { ChangeEvent, useState } from 'react';

import { useForm } from '@/hooks/useForm';

import { GatedContentProps } from '@/components/organisms/gated-content/types';

import { cmsApiEndpoint } from '@/constant/env';
import { EMAIL_REGEX } from '@/features/jobApplicationForm/constants';
import { apiCall } from '@/utils/fetch';

import { getSanitizedGateKey, setGateCleared } from './utils';

const DEFAULT_ERROR_MESSAGE =
  'Oops, an error occurred, please try again later.';

export type CaptureUserDetailsState = {
  email: string;
};

export type CaptureUserOptions = {
  context?: 'leadPopUp' | 'default';
  onSuccess?: () => void;
  onError?: () => void;
};

const useCaptureUserDetails = (
  props: GatedContentProps,
  options?: CaptureUserOptions
) => {
  const [apiError, setApiError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const gateId = getSanitizedGateKey(props.gateIdentifier);

  const form = useForm<CaptureUserDetailsState>({
    initialValues: { email: '' },
    validateOnChange: true,
    validateOnBlur: true,
    validator: {
      email: (value) => {
        if (!value) return 'This field is required';
        if (typeof value !== 'string' || !EMAIL_REGEX.test(value)) {
          return 'Please provide a valid email address';
        }
      },
    },
  });

  const { handleSubmit: onFormSubmit, setFieldValue, values } = form;

  const handleSubmit = async () => {
    setApiError(null);
    setIsLoading(true);

    try {
      const response = await apiCall<any>({
        url: cmsApiEndpoint + '/users/capture_details',
        method: 'POST',
        body: {
          activeCampaignListId: props.activeCampaignListId,
          email: values.email,
          saveExistingEmails: props.saveExistingEmails,
        },
      });

      setIsLoading(false);

      if (!response || response?.error) {
        setApiError(response?.error || DEFAULT_ERROR_MESSAGE);

        if (options?.context === 'leadPopUp' && options?.onError) {
          options.onError();
        }
        return;
      }

      if (options?.context !== 'leadPopUp') {
        setGateCleared(props.gateIdentifier);
      }

      if (options?.context === 'leadPopUp' && options?.onSuccess) {
        options.onSuccess();
      }
    } catch (error: any) {
      setIsLoading(false);
      setApiError(error?.message || DEFAULT_ERROR_MESSAGE);
      console.error(error);

      if (options?.context === 'leadPopUp' && options?.onError) {
        options.onError();
      }
    }
  };

  const getSetFieldValue =
    (name: keyof CaptureUserDetailsState) =>
    (e: ChangeEvent<HTMLInputElement>) =>
      setFieldValue(name, e.target.value);

  return {
    ...form,
    errors: {
      email: form.errors?.email || apiError,
    },
    gateId,
    isLoading,
    handleSubmit: onFormSubmit(handleSubmit),
    getSetFieldValue,
  };
};

export default useCaptureUserDetails;
